import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { LangToggle, Logo } from "./kit";
import { Button } from "./ui/button";

export default function PublicLayout() {
  const { t } = useT();
  const { user, brand } = useAuth();
  const [open, setOpen] = useState(false);
  const links = [["/#features", t("nav.features")], ["/pricing", t("nav.pricing")], ["/docs", t("nav.docs")], ["/guides", t("nav.guides")], ["/blog", t("nav.blog")], ["/support", t("nav.support")]];
  return (
    <div className="grain relative min-h-screen overflow-x-clip bg-[#050507]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#070709]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="nav-home"><Logo /></Link>
          <nav className="hidden items-center gap-7 md:flex">
            {links.map(([to, label]) => (
              <NavLink key={to} to={to} className="text-sm text-zinc-400 transition-colors hover:text-white" data-testid={`nav-link-${label.toLowerCase()}`}>{label}</NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 md:flex">
            <LangToggle />
            {user ? (
              <Button asChild size="sm" data-testid="nav-open-app"><Link to="/app">{t("nav.dashboard")}</Link></Button>
            ) : (
              <>
                <Link to="/login" className="text-sm text-zinc-300 hover:text-white" data-testid="nav-signin">{t("nav.signin")}</Link>
                <Button asChild size="sm" className="rounded-full" data-testid="nav-signup"><Link to="/register">{t("nav.signup")}</Link></Button>
              </>
            )}
          </div>
          <button className="md:hidden text-zinc-300" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <div className="space-y-3 border-t border-white/10 px-4 py-4 md:hidden" data-testid="nav-mobile-menu">
            {links.map(([to, label]) => <Link key={to} to={to} onClick={() => setOpen(false)} className="block text-zinc-300">{label}</Link>)}
            <div className="flex items-center gap-3 pt-2"><LangToggle prefix="mobile-" />
              <Link to={user ? "/app" : "/login"} className="text-sm text-purple-300">{user ? t("nav.dashboard") : t("nav.signin")}</Link>
              {!user && <Button asChild size="sm" className="rounded-full" data-testid="nav-mobile-signup"><Link to="/register" onClick={() => setOpen(false)}>{t("nav.signup")}</Link></Button>}</div>
          </div>
        )}
      </header>
      <main className="relative z-10"><Outlet /></main>
      <footer className="relative z-10 border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-10 text-sm text-zinc-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <Logo testId="footer-logo" />
          <div className="flex flex-wrap gap-6">
            <Link to="/docs" className="hover:text-white">{t("nav.docs")}</Link>
            <Link to="/guides" className="hover:text-white" data-testid="footer-guides">{t("nav.guides")}</Link>
            <Link to="/blog" className="hover:text-white" data-testid="footer-blog">{t("nav.blog")}</Link>
            <Link to="/pricing" className="hover:text-white" data-testid="footer-pricing">{t("nav.pricing")}</Link>
            <Link to="/docs/smtp" className="hover:text-white" data-testid="footer-smtp">SMTP</Link>
            <Link to="/support" className="hover:text-white" data-testid="footer-support">{t("nav.support")}</Link>
            {brand.status_url ? <a href={brand.status_url} className="hover:text-white" data-testid="footer-status">Status</a>
              : <span title="A public status page has not been published yet" data-testid="footer-status">Status page: not published</span>}
          </div>
          <span>© {new Date().getFullYear()} {brand.product_name}</span>
        </div>
      </footer>
    </div>
  );
}
